import { useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UpgradeNotice } from "@/components/subscription/UpgradeNotice";
import { useSubscription } from "@/hooks/useSubscription";

interface SignatureDialogProps {
  documentId: string;
  documentName: string;
  open: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

export function SignatureDialog({
  documentId,
  documentName,
  open,
  onSuccess,
  onClose,
}: SignatureDialogProps) {
  const { profile } = useAuth();
  const { entitlements } = useSubscription();
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signerName = useMemo(() => {
    if (!profile) return "";
    return `${profile.first_name} ${profile.last_name}`.trim();
  }, [profile]);

  const handleClear = () => {
    signatureRef.current?.clear();
  };

  const handleSign = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Please add your signature before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("sign-document", {
        body: {
          document_id: documentId,
          signature_data_url: signatureRef.current.toDataURL("image/png"),
          signer_name: signerName || "Electronic Signer",
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Document signed successfully");
      onSuccess();
    } catch (err) {
      toast.error("Signing failed", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Sign {documentName}</DialogTitle>
          <DialogDescription>
            Draw your signature below to complete this document electronically.
          </DialogDescription>
        </DialogHeader>

        {!entitlements.electronicSignatures ? (
          <>
            <UpgradeNotice
              title="Electronic signatures require Professional"
              description="Upgrade to Professional or Enterprise to sign documents electronically with PropTRUST's SHA-256 signature trail."
            />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-border bg-card p-3">
              <SignatureCanvas
                ref={signatureRef}
                penColor="hsl(var(--foreground))"
                canvasProps={{
                  className: "h-64 w-full rounded-lg border border-border bg-background",
                }}
              />
            </div>

            <p className="text-sm text-muted-foreground">
              By signing, you agree this constitutes a legally binding electronic signature.
            </p>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClear} disabled={isSubmitting}>
                Clear
              </Button>
              <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSign} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing...
                  </>
                ) : (
                  "Sign Document"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
